/** Operational copy. Financial state comes from WMF; these are next steps, not decisions. */
export interface CreditReviewGuidance {
  money: string;
  spendability: string;
  next: string;
}

const PURCHASE_HELD = "The purchase record is retained; this review does not create another charge or a refund.";
const NO_BLIND_RETRY = "Check the recorded state before retrying a purchase, booking or credit change.";

const GUIDANCE: Record<string, CreditReviewGuidance> = {
  legacy_credit_hold: {
    money: "The original paid purchase and credit history remain recorded.",
    spendability: "These older credits are held until their original coupon and used lessons are verified.",
    next: "Open the student's Admin credits, choose Review legacy credits, and verify the original issuance record. Preview the checked release before activating the block.",
  },
  purchase_student_unresolved: {
    money: PURCHASE_HELD,
    spendability: "Credits are held until the right student is confirmed.",
    next: "Match the original purchase booking in Unmatched, then check its payment and existing credit history. Complete a purchase claim if one is listed. Older purchases may need legacy coupon reconciliation. Do not sell the block again.",
  },
  purchase_payment_not_settled: {
    money: "Payment has not been confirmed as settled; do not assume funds were received.",
    spendability: "No new lesson credits are spendable from this purchase.",
    next: "Check the original payment in Amelia and the payment provider before trying the purchase import again.",
  },
  purchase_payment_reversed: {
    money: "The original payment was reversed or marked unpaid; check its current status in Amelia.",
    spendability: "Unused local credits are frozen while the payment hold is open; past lessons remain recorded.",
    next: "Resolve the payment with the office. Clear the hold only after the original payment is verified as paid; never make a new grant to compensate.",
  },
  refund_amount_exceeds_unused_entitlement: {
    money: "A refund exceeds the value of unused entitlement; the difference needs a human decision.",
    spendability: "This review does not itself freeze credits. Check the block’s current status and available balance before taking action.",
    next: "Compare the original payment, refund and used lessons before making any adjustment. Do not repeat the refund.",
  },
  credit_ready_access_requires_review: {
    money: PURCHASE_HELD,
    spendability: "The local credits remain on the block, but the family may not yet have Hub access.",
    next: "Check the student's confirmed account and family links. Fix the identity or invitation issue; the notification worker will retry.",
  },
  credit_ready_delivery_failed: {
    money: PURCHASE_HELD,
    spendability: "The credits remain in WMF; Hub access or the email may still be pending.",
    next: "Check the Hub and email outbox, then let the notification worker retry. Do not recreate the purchase.",
  },
  cancellation_credit_decision_required: {
    money: "The original payment and booking history remain recorded; this decision does not make a refund.",
    spendability: "A returned credit is not available until the cancellation decision is recorded.",
    next: "Check the cancellation evidence, then choose Return credit or Keep credit used once.",
  },
  cancellation_redecision_required: {
    money: "The original payment remains recorded; this review does not move money.",
    spendability: "The prior credit decision still applies until staff reconcile the repeated cancellation.",
    next: "Inspect the lesson and its earlier decision. Do not return a second credit blindly.",
  },
  paid_cancellation_credit_requires_attention: {
    money: "The paid lesson remains in history; no new payment or refund is made here.",
    spendability: "The rebooking credit has not been issued yet.",
    next: "Verify the cancellation, then use Issue rebooking credit for this specific lesson. Do not add a generic credit.",
  },
  credit_history_inconsistent: {
    money: "The ledger history needs reconciliation; this review makes no payment change.",
    spendability: "Do not assume the displayed balance is safe to spend until history is reconciled.",
    next: "Compare grants, uses, restores, refunds and Amelia bookings. Avoid manual balance changes until the discrepancy is explained.",
  },
  coupon_capacity_requires_attention: {
    money: "The payment record is unchanged; legacy coupon capacity may disagree with the local history.",
    spendability: "Legacy coupon spendability is uncertain until Amelia confirms the capacity change.",
    next: "Check the coupon in Amelia and the block history. Do not activate local spending while the coupon may still work.",
  },
  coupon_restoration_requires_attention: {
    money: "The payment record is unchanged; coupon restoration has not been confirmed.",
    spendability: "The legacy coupon's remaining uses may be wrong.",
    next: "Check the cancellation and coupon usage in Amelia before returning any credit manually.",
  },
  coupon_conversion_requires_review: {
    money: "The original payment and ledger history remain recorded; conversion makes no charge or refund.",
    spendability: "The block is held while Amelia coupon spendability is uncertain. Local credits cannot be booked.",
    next: "Compare the coupon's status, limit and uses in Amelia with the block ledger. Rerun the bounded conversion only when they match; do not change authority by hand.",
  },
  lesson_credit_unmatched: {
    money: "No payment change was made by this review.",
    spendability: "The credit outcome cannot be confirmed until the booking is matched.",
    next: `Match the lesson to the right student and reconcile the booking. ${NO_BLIND_RETRY}`,
  },
};

export function creditReviewGuidance(kind: string): CreditReviewGuidance {
  return GUIDANCE[kind] ?? {
    money: "This review does not charge or refund the family. Verify the original payment separately.",
    spendability: "Check the block's current authority and balance before promising a credit.",
    next: `Inspect the block and appointment history. ${NO_BLIND_RETRY}`,
  };
}
